import { StateFieldsForm } from "components/Forms/StateFieldsForm";

import { UnfinishedChangesPrompt } from "components/UnfinishedChangesPrompt";
import { Stack, Typography } from "@mui/material";
import { ReportFloatingBar } from "../ReportFloatingBar";

import { ProgramArea } from "generated-graphql/graphql";
import { TierIiReportStep } from "generated-graphql/graphql";
import { useFacilityInputValidation } from "../validationHooks/useFacilityInputValidation";
import { useStateInfoIssues, useUpdateFacilityReport } from "../useReport";
import { useCallback, useMemo } from "react";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useValidatingForm } from "hooks/useValidatingForm";
import { FormProvider } from "react-hook-form";
import { useTouchReportMutation } from "../useTouchReportMutation";
import { useReport } from "../useReport";
import { useNavigateReportSaveCancel } from "../useNavigateReportSaveCancel";
import { FacilityDetailsInput } from "routes/Customer/Facility/FacilityProfile";
import { omit } from "lodash-es";
import { transform as transformStateField } from "hooks/transform/transformStateField";
import { StateInfoSkelly } from "./StateInfoSkelly";
import { useReportIsVerified } from "../useReportIsVerified";

export function StateInformationForm() {
  const alerts = useAlerts();
  const navigateToOverview = useNavigateReportSaveCancel();
  const { data, loading } = useReport();
  const { handleSave: handleTouch, loading: touchLoading } =
    useTouchReportMutation(
      data?.tierIIReport.id ?? "",
      data?.tierIIReport.touchedSteps ?? [],
      TierIiReportStep.StateInformation
    );

  const dynamicFields = useMemo(
    () => data?.tierIIReport.facility.stateFields ?? [],
    [data?.tierIIReport.facility.stateFields]
  );

  const defaultValues: FacilityDetailsInput = useMemo(
    () => ({
      state: data?.tierIIReport.facility.state,
      lepcId: data?.tierIIReport.facility.lepcId,
      fireDepartmentId: data?.tierIIReport.facility.fireDepartmentId,
      stateFields: dynamicFields.map((sf) => ({
        ...omit(sf, "selectOptions", "tooltip"),
        value: sf.value ?? null,
        type: ProgramArea.Epcra,
      })),
    }),
    [data, dynamicFields]
  );
  const form = useValidatingForm(
    defaultValues,
    useStateInfoIssues(),
    useFacilityInputValidation({
      facilityId: data?.tierIIReport.facility.id ?? "",
      reportId: data?.tierIIReport.id,
      pick: ["stateFields"],
    }),
    undefined,
    {
      isDisplayOnly: useReportIsVerified(),
    }
  );

  const [updateFacility, { loading: submitting }] = useUpdateFacilityReport();

  const {
    formState: { isDirty, isSubmitSuccessful, isSubmitted },
  } = form;

  const hasPendingChanges = isDirty && !isSubmitSuccessful && !isSubmitted;

  const onSubmit = useCallback(
    async (input: FacilityDetailsInput) => {
      if (!data) return;
      const saveData = {
        ...input,
        stateFields: input.stateFields?.map(transformStateField),
      };

      try {
        await updateFacility({
          variables: {
            id: data.tierIIReport.facility.id,
            facility: saveData,
            reportId: data.tierIIReport.id,
            programArea: ProgramArea.Epcra,
          },
          awaitRefetchQueries: true,
        });
        alerts.success("State information saved successfully.");
        await handleTouch();
      } catch (err) {
        alerts.error("Error saving state information.");
        console.info("Error saving state information.", err);
      }
    },
    [alerts, data, handleTouch, updateFacility]
  );

  return (
    <FormProvider {...form}>
      <form style={{ flexGrow: 1 }}>
        <Stack
          sx={{
            height: "100%",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <StateInfoSkelly />
          ) : dynamicFields.length === 0 ? (
            <Typography variant="body1">
              There are no additional state fields for this facility
            </Typography>
          ) : (
            <StateFieldsForm
              context="facility"
              fields={data?.tierIIReport?.facility.stateFields ?? []}
              namePrefix="stateFields"
            />
          )}
        </Stack>
      </form>
      <ReportFloatingBar
        saving={submitting}
        onSaveClick={form.handleSubmit(onSubmit)}
        loading={loading || touchLoading}
        onCancel={navigateToOverview}
        issues={form.issues}
      />
      <UnfinishedChangesPrompt when={hasPendingChanges} />
    </FormProvider>
  );
}
